import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, {
  DateClickArg,
  EventResizeDoneArg,
} from '@fullcalendar/interaction'
import svLocale from '@fullcalendar/core/locales/sv'
import timeGridPlugin from '@fullcalendar/timegrid'
import { EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core'
import {
  Alert,
  Box,
  FormControlLabel,
  Snackbar,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { doc, Timestamp, updateDoc } from 'firebase/firestore'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { firestore } from '../../firebaseInit'
import { useWorkouts, Workout } from '../../../providers/workouts'
import { useWorkoutSettings, WorkoutType } from '../../../providers/workout-settings'

const calendarRoute = '/admin?tab=calendar'

function getDateClickRange(arg: DateClickArg) {
  const start = new Date(arg.date)

  if (arg.allDay) {
    start.setHours(9, 0, 0, 0)
  }

  const end = new Date(start)
  end.setHours(end.getHours() + 1)

  return { start, end }
}

function mapWorkoutToEvent(
  workout: Workout,
  getWorkoutTypeName: (id: string) => string,
  getWorkoutType: (id: string) => WorkoutType
): EventInput {
  const workoutType = getWorkoutType(workout.type)
  const color = workoutType.color ?? '#b67943'

  return {
    id: workout.id,
    title: getWorkoutTypeName(workout.type),
    start: workout.startTime.toDate(),
    end: workout.endTime.toDate(),
    backgroundColor: color,
    borderColor: color,
    textColor: '#ffffff',
    extendedProps: {
      location: workout.location,
      participants: `${workout.participantsCount}/${workout.numberOfPlaces}`,
    },
  }
}

async function updateWorkoutTime(
  id: string,
  start: Date,
  end: Date | null
) {
  const safeEnd = end ?? new Date(start.getTime() + 60 * 60 * 1000)

  await updateDoc(doc(firestore, 'workouts', id), {
    date: Timestamp.fromDate(start),
    startTime: Timestamp.fromDate(start),
    endTime: Timestamp.fromDate(safeEnd),
  })
}

export default function WorkoutCalendar() {
  const navigate = useNavigate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { workouts, loading, error } = useWorkouts()
  const { getWorkoutType, getWorkoutTypeName } = useWorkoutSettings()
  const [showFullDay, setShowFullDay] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    message: string
    severity: 'success' | 'error'
  } | null>(null)

  const events = useMemo(
    () =>
      workouts.map((workout) =>
        mapWorkoutToEvent(workout, getWorkoutTypeName, getWorkoutType)
      ),
    [getWorkoutType, getWorkoutTypeName, workouts]
  )

  const handleDateClick = (arg: DateClickArg) => {
    const { start, end } = getDateClickRange(arg)

    navigate('/workout', {
      state: {
        returnTo: calendarRoute,
        initialStart: start.toISOString(),
        initialEnd: end.toISOString(),
      },
    })
  }

  const handleEventClick = (arg: EventClickArg) => {
    navigate(`/workout/${arg.event.id}`, {
      state: { returnTo: calendarRoute },
    })
  }

  const handleEventMove = async (
    id: string,
    start: Date | null,
    end: Date | null,
    revert: () => void
  ) => {
    if (!start) {
      revert()
      return
    }

    try {
      await updateWorkoutTime(id, start, end)
      setSnackbar({ message: 'Passet uppdaterades', severity: 'success' })
    } catch (error) {
      console.log(error)
      revert()
      setSnackbar({
        message: 'Passet kunde inte uppdateras',
        severity: 'error',
      })
    }
  }

  const handleEventDrop = (arg: EventDropArg) =>
    handleEventMove(arg.event.id, arg.event.start, arg.event.end, arg.revert)

  const handleEventResize = (arg: EventResizeDoneArg) =>
    handleEventMove(arg.event.id, arg.event.start, arg.event.end, arg.revert)

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        '& .fc': {
          color: 'text.primary',
          fontFamily: 'inherit',
        },
        '& .fc .fc-toolbar': {
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
        },
        '& .fc .fc-toolbar-title': {
          fontSize: { xs: '1.1rem', sm: '1.35rem' },
          fontWeight: 700,
        },
        '& .fc .fc-button': {
          backgroundColor: 'primary.main',
          borderColor: 'primary.main',
          borderRadius: 1,
          boxShadow: 'none',
          color: 'primary.contrastText',
          textTransform: 'none',
        },
        '& .fc .fc-button:hover, & .fc .fc-button:focus': {
          backgroundColor: 'primary.dark',
          borderColor: 'primary.dark',
        },
        '& .fc .fc-button:disabled': {
          backgroundColor: 'action.disabledBackground',
          borderColor: 'action.disabledBackground',
          color: 'text.disabled',
        },
        '& .fc-theme-standard td, & .fc-theme-standard th, & .fc-theme-standard .fc-scrollgrid':
          {
            borderColor: 'divider',
          },
        '& .fc .fc-daygrid-day.fc-day-today, & .fc .fc-timegrid-col.fc-day-today':
          {
            backgroundColor: 'rgba(182, 121, 67, 0.08)',
          },
        '& .fc .fc-event': {
          borderRadius: 1,
          cursor: 'pointer',
          padding: '2px 4px',
        },
        '& .fc .fc-event-main': {
          color: 'inherit',
        },
      }}
    >
      {error && (
        <Typography color="error" sx={{ pb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showFullDay}
              onChange={(event) => setShowFullDay(event.target.checked)}
            />
          }
          label="Visa hela dygnet"
        />
      </Box>

      <FullCalendar
        allDaySlot={false}
        buttonText={{
          today: 'Idag',
          month: 'Månad',
          week: 'Vecka',
          day: 'Dag',
        }}
        dateClick={handleDateClick}
        editable
        eventClick={handleEventClick}
        eventContent={(arg) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              component="div"
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {arg.timeText && `${arg.timeText} `}
              {arg.event.title}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: '0.68rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {arg.event.extendedProps.location}
              {arg.event.extendedProps.participants
                ? ` · ${arg.event.extendedProps.participants}`
                : ''}
            </Typography>
          </Box>
        )}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        events={events}
        firstDay={1}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        height="auto"
        initialView={smallScreen ? 'timeGridDay' : 'timeGridWeek'}
        loading={(isLoading) => {
          if (isLoading && !loading) return
        }}
        locale={svLocale}
        nowIndicator
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        selectable
        slotDuration="00:30:00"
        slotLabelFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
        }}
        slotMinTime={showFullDay ? '00:00:00' : '06:00:00'}
        slotMaxTime={showFullDay ? '24:00:00' : '22:00:00'}
        weekends
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}
